import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import { CircleAlert, Eye, EyeOff, Loader2, TrendingUp } from "lucide-react";

import { Button } from "./Button";
import { Input } from "./Input";
import { Label } from "./Label";
import { login } from "../utilities/api";

const controlClass =
	"h-9 rounded-md border-slate-200 bg-white text-sm text-slate-900 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-0";

export default function Login() {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState(null);
	const navigate = useNavigate();

	const mutation = useMutation({
		mutationFn: login,
		onSuccess: () => {
			navigate("/"); // Navigate to dashboard or home
		},
		onError: (err) => {
			if (err?.response?.status === 401) {
				setError("Incorrect username or password. Try again.");
				return;
			}
			if (err?.message === "Network Error") {
				setError("Couldn't reach the server. Check your connection and try again.");
				return;
			}
			setError(err instanceof Error ? err.message : "Sign in failed. Try again.");
		},
	});
	const isLoading = mutation.isLoading;

	const handleLogin = (e) => {
		e.preventDefault();
		setError(null);
		mutation.mutate({ username, password });
	};

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-10">
			<div className="w-full max-w-sm">
				<div className="mb-6 flex items-center justify-center gap-2 font-semibold text-slate-900">
					<span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white">
						<TrendingUp className="h-4 w-4" aria-hidden="true" />
					</span>
					Portfolio
				</div>

				<div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
					<h1 className="text-lg font-semibold text-slate-900">Sign in</h1>
					<p className="mt-1 text-sm text-slate-500">
						Enter your credentials to open your portfolio.
					</p>

					<form onSubmit={handleLogin} className="mt-6 space-y-4" aria-busy={isLoading}>
						<div>
							<Label htmlFor="username" className="mb-1 block text-slate-700">
								Username
							</Label>
							<Input
								id="username"
								name="username"
								type="text"
								value={username}
								onChange={(e) => {
									setUsername(e.target.value);
									setError(null);
								}}
								required
								autoFocus
								autoComplete="username"
								autoCapitalize="none"
								autoCorrect="off"
								spellCheck="false"
								aria-invalid={Boolean(error)}
								aria-describedby={error ? "login-error" : undefined}
								className={controlClass}
							/>
						</div>

						<div>
							<Label htmlFor="password" className="mb-1 block text-slate-700">
								Password
							</Label>
							<div className="relative">
								<Input
									id="password"
									name="password"
									type={showPassword ? "text" : "password"}
									value={password}
									onChange={(e) => {
										setPassword(e.target.value);
										setError(null);
									}}
									required
									autoComplete="current-password"
									aria-invalid={Boolean(error)}
									aria-describedby={error ? "login-error" : undefined}
									className={`${controlClass} pr-10`}
								/>
								<button
									type="button"
									onClick={() => setShowPassword((prev) => !prev)}
									aria-label={showPassword ? "Hide password" : "Show password"}
									aria-pressed={showPassword}
									aria-controls="password"
									className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
								>
									{showPassword ? (
										<EyeOff className="h-4 w-4" aria-hidden="true" />
									) : (
										<Eye className="h-4 w-4" aria-hidden="true" />
									)}
								</button>
							</div>
						</div>

						{error && (
							<div
								id="login-error"
								role="alert"
								className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
							>
								<CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
								<span>{error}</span>
							</div>
						)}

						<Button type="submit" className="h-9 w-full" disabled={isLoading}>
							{isLoading && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
							)}
							{isLoading ? "Signing in..." : "Sign in"}
						</Button>
					</form>
				</div>
			</div>
		</div>
	);
}
